import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import UserContext from '@context/User/user-context';
import ButtonSecondary from '@components/shared/buttons/ButtonSecondary/ButtonSecondary';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import useAddToCart from '@hooks/User/useAddToCart';
import AddedToCartConfirmationModalContext from '@context/Modal/added-to-cart-confirmation-modal-context';
import { IVendorVariantInternal } from '@models/VendorVariant/i-vendor-variant';
import { isOverstockVendor } from '@components/shared/shop/model/Vendors';
import Check from './check.svg';
import styles from './AddToCartButton.module.scss';

interface IAddToCartButtonProps {
  analytics: {
    listId?: string;
    location: string;
  };
  boardId?: number;
  product: IVendorVariantInternal;
  className?: string;
}

const AddToCartButton = ({
  className = '',
  product,
  boardId,
  analytics,
}: IAddToCartButtonProps) => {
  const {
    user,
    cartedVendorVariantIds,
  } = useContext(UserContext);

  const _boardId = boardId ? boardId.toString() : undefined;

  const { open: openLoginRegisterModal } = useContext(LoginRegisterModalContext);

  const router = useRouter();

  const [addToCart, { busy: addingToCart, isDoneAddingToCart }]
    = useAddToCart({ location: analytics.location, vendorVariant: product });

  const { open: openAddedToCartConfirmation } = useContext(AddedToCartConfirmationModalContext);

  const [triggeredAddToCart, setTriggeredAddToCart] = useState(false);

  const inStock = product.availability === 'in_stock' || product.availability === 'back_ordered';

  const vendorId = parseInt(product.vendor.id, 10);

  useEffect(() => {
    if (triggeredAddToCart && isDoneAddingToCart) {
      openAddedToCartConfirmation({ vendorVariant: product });
      setTriggeredAddToCart(false);
    }
  }, [triggeredAddToCart, isDoneAddingToCart]);

  const handleAddToCart = async () => {
    const { listId } = analytics;

    if (isOverstockVendor(vendorId)) {
      return window.open(product.weblink, '_blank');
    }

    const doAddToCart = async () => addToCart({ listId, boardId: _boardId });

    if (!user) {
      return openLoginRegisterModal({
        onLoginSuccess: doAddToCart,
        onRegisterSuccess: doAddToCart,
        magicLinkRedirectUrl: router.asPath,
        source: analytics.location,
        loginTitle: 'Explore inspiration and design your dream space',
        registerTitle: 'Join us! Get Inspired and design your dream space',
      });
    } else {
      setTriggeredAddToCart(true);
      return doAddToCart();
    }
  };

  const [cartButtonLabel, setCartButtonLabel] = useState<string | React.ReactNode>('Add to cart');
  useEffect(() => {
    if (addingToCart) {
      setCartButtonLabel('Adding to cart');
    } else if (isOverstockVendor(vendorId)) {
      setCartButtonLabel('Shop on Overstock');
    } else if (cartedVendorVariantIds[product.id]) {
      setCartButtonLabel(
        <span className={styles.AddedCheck}>
          <Check /> Added!
        </span>
      );
      setTimeout(() => {
        setCartButtonLabel('Add to cart');
      }, 3000);
    }
  }, [addingToCart, cartedVendorVariantIds]);

  return (
    <ButtonSecondary
      className={className}
      disabled={addingToCart || !inStock}
      onClick={() => { handleAddToCart(); } }
    >
      {cartButtonLabel}
    </ButtonSecondary>
  );
};

export default AddToCartButton;
